import type { PhilosopherMetadata, PineconeVector, PineconeQueryResponse } from '../types/pinecone';

export class PineconeServiceBrowser {
  private apiKey: string;
  private indexName: string;
  private indexHost: string;
  
  constructor() {
    this.apiKey = import.meta.env.VITE_PINECONE_API_KEY;
    this.indexName = import.meta.env.VITE_PINECONE_INDEX_NAME || 'philosopher-corpus';
    // For serverless indexes, the host follows a different pattern
    this.indexHost = import.meta.env.VITE_PINECONE_INDEX_HOST;
    
    if (!this.apiKey) {
      throw new Error('VITE_PINECONE_API_KEY is required. Please configure environment variables or implement backend API.');
    }
    
    if (!this.indexHost) {
      throw new Error('VITE_PINECONE_INDEX_HOST is required. Please configure environment variables or implement backend API.');
    }
  }

  private getIndexUrl(): string {
    return `https://${this.indexHost}`;
  }

  private getHeaders() {
    return {
      'Content-Type': 'application/json',
      'Api-Key': this.apiKey,
    };
  }

  async query(
    vector: number[],
    philosopher?: 'adam-smith' | 'karl-marx',
    topK: number = 10
  ): Promise<PineconeQueryResponse> {
    const url = `${this.getIndexUrl()}/query`;
    
    const body: {
      vector: number[];
      topK: number;
      includeMetadata: boolean;
      filter?: { philosopher: string };
    } = {
      vector,
      topK,
      includeMetadata: true,
    };
    
    if (philosopher) {
      body.filter = { philosopher };
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Pinecone query failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      return {
        matches: data.matches.map((match: {
          id: string;
          score?: number;
          metadata?: Record<string, unknown>;
        }) => ({
          id: match.id,
          score: match.score || 0,
          metadata: {
            philosopher: match.metadata?.philosopher as 'adam-smith' | 'karl-marx',
            work_title: match.metadata?.work_title as string,
            chapter: match.metadata?.chapter as string | undefined,
            section: match.metadata?.section as string | undefined,
            page_number: match.metadata?.page_number as number | undefined,
            chunk_index: match.metadata?.chunk_index as number,
            total_chunks: match.metadata?.total_chunks as number,
            timestamp: match.metadata?.timestamp as string,
            version: match.metadata?.version as string,
            text: match.metadata?.text as string,
          } as PhilosopherMetadata,
          text: match.metadata?.text as string, // Also include at top level for compatibility
        })),
      };
    } catch (error) {
      console.error('Pinecone query error:', error);
      throw error;
    }
  }

  async upsertVectors(vectors: PineconeVector[]) {
    const url = `${this.getIndexUrl()}/vectors/upsert`;
    
    // Convert metadata to be compatible with Pinecone's RecordMetadata
    const pineconeVectors = vectors.map(vector => ({
      id: vector.id,
      values: vector.values,
      metadata: {
        philosopher: vector.metadata.philosopher,
        work_title: vector.metadata.work_title,
        chapter: vector.metadata.chapter || '',
        section: vector.metadata.section || '',
        page_number: vector.metadata.page_number || 0,
        text: vector.metadata.text,
        chunk_index: vector.metadata.chunk_index,
        total_chunks: vector.metadata.total_chunks,
        timestamp: vector.metadata.timestamp,
        version: vector.metadata.version,
      }
    }));

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ vectors: pineconeVectors }),
      });

      if (!response.ok) {
        throw new Error(`Pinecone upsert failed: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Pinecone upsert error:', error);
      throw error;
    }
  }

  async describeIndexStats() {
    const url = `${this.getIndexUrl()}/describe_index_stats`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error(`Pinecone stats failed: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Pinecone stats error:', error);
      throw error;
    }
  }

  async deleteAll() {
    const url = `${this.getIndexUrl()}/vectors/delete`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ deleteAll: true }),
      });

      if (!response.ok) {
        throw new Error(`Pinecone delete failed: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Pinecone delete error:', error);
      throw error;
    }
  }
}

let _pineconeServiceBrowser: PineconeServiceBrowser | null = null;

export const pineconeServiceBrowser = {
  get instance(): PineconeServiceBrowser {
    if (!_pineconeServiceBrowser) {
      _pineconeServiceBrowser = new PineconeServiceBrowser();
    }
    return _pineconeServiceBrowser;
  },
  
  // Proxy methods for backward compatibility
  async query(vector: number[], philosopher?: 'adam-smith' | 'karl-marx', topK: number = 10): Promise<PineconeQueryResponse> {
    return this.instance.query(vector, philosopher, topK);
  },
  
  async upsertVectors(vectors: PineconeVector[]): Promise<{ upsertedCount: number }> {
    return this.instance.upsertVectors(vectors);
  },
  
  async describeIndexStats(): Promise<{ dimension: number; indexFullness: number; totalVectorCount?: number; totalRecordCount?: number }> {
    return this.instance.describeIndexStats();
  },
  
  async deleteAll(): Promise<Record<string, unknown>> {
    return this.instance.deleteAll();
  }
};